import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import * as Sentry from '@sentry/nextjs';
import { FeaturedNewsApi, NavBarContentData, HeadTags } from '@/shared/types/home.types';
import { api, validatesResponse } from '@/services/api';
import { canonicalizeDirectusVideo, fetchMateriaFromDirectus } from '@/services/directusMateria';
import LazyLoad from 'react-lazyload';
import useBreakpoint from '@/hooks/useBreakpoint';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import Layout from '@/components/Layout';
import { NewsDataType } from '@/pages/[categorias]/[...news]';
import { BreadcrumbItem } from '@/components/Layout/Breadcrumb';
import { SchemaType } from '@/components/Internals/InternalSchema';

const DIRECTUS_CACHE_TTL_S = 120;

const NewsItem = dynamic(() => import('@/components/Internals/NewsItem'));

interface Video {
  id: string;
  idYoutube: string;
  titulo: string;
  descricao: string;
  duracao: string;
  views: string;
  status: string;
  canal: string;
}

interface Playlist {
  id: string;
  titulo: string;
  nomeUrl: string;
  status: string;
}

interface VideoDataType extends NewsDataType {
  video: Video;
  playlist: Playlist;
}

interface videosProps {
  // eslint-disable-next-line react/no-unused-prop-types
  responseData: {
    data: VideoDataType;
    segmentacoes: [];
    breadcrumb: BreadcrumbItem[];
    ssr: HeadTags[];
    paginacao?: string;
    schema?: SchemaType;
  };
  navBarData?: any;
}

// eslint-disable-next-line no-empty-pattern
export default function Videos({ responseData, navBarData }: videosProps): JSX.Element {
  const AdMgid = dynamic(() => import('@/components/Ads/AdMgid'), { ssr: false });

  const { data, ssr, schema } = responseData;
  const { segmentacoes } = data;
  const [isTabletOrMobile] = useBreakpoint('tablet');

  const dataLayer = {
    category: data?.categoriaPrincipal || null,
    contentType: data.tipo,
  };

  return (
    <Layout
      layoutData={{
        segmentacoes,
        tagsData: ssr,
        navBarData,
        schema,
        contentType: data.tipo,
        breadcrumbList: data.breadcrumb,
      }}
      headingTag="span"
      dataLayer={dataLayer}
    >
      <NewsItem newsData={data} isCurrentPage type="video" />
      <LazyLoad height={1}>
        <AdMgid type={isTabletOrMobile ? 'mobile' : 'desktop'} />
      </LazyLoad>
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { canais, videos } = context.query;
  const { host, referer } = context.req.headers;
  const slug = Array.isArray(videos) ? videos[0] : videos;
  const channel = Array.isArray(canais) ? canais[0] : canais;

  if (!slug || !channel) return { notFound: true };

  // Dispara o menu em paralelo com a busca no Directus pra não somar latências.
  // A mesma promise é reaproveitada pelo fallback Mono (getConcurrentPromises)
  // — promises resolvem uma única vez, então não há request duplicada.
  const responseMenuLink = `/componentes/menu/`;
  const responseMenuRequest = api.get<NavBarContentData>(responseMenuLink);

  // Vídeos publicados no Directus podem carregar slugs de canal que não
  // existem na tabela videoPlaylist do Mono (ex: slug literal 'videos'),
  // fazendo o /video/<canal>/<slug>/ no Mono retornar 404. Tenta o Directus
  // primeiro; se a matéria estiver lá, devolve sem encostar no Mono.
  try {
    const directusResponse = await fetchMateriaFromDirectus(channel, slug);
    if (directusResponse && directusResponse.data?.status === 0) {
      const pathName = `/video/${channel}/${slug}/`;
      // Canonicaliza no host de vídeo: apex → 301 pro video.<host> (destino usa a
      // URL canônica do payload, que pra vídeo Lisa é a rota plana /video/<slug>/);
      // nos demais hosts injeta `dominio` pro validatesResponse servir 200.
      const canonical = canonicalizeDirectusVideo(directusResponse, host, pathName);
      if (canonical) return canonical;

      const treated = validatesResponse(directusResponse, false, false, '', host, referer, pathName);
      if (treated.data.redirect || treated.data.notFound) return treated.data;

      const responseMenu = await responseMenuRequest.catch(error => {
        Sentry.captureException(error, {
          tags: { source: 'menu-video-page' },
          extra: { canal: channel, slug },
        });
        return null;
      });
      const fallbackHeaders = {
        'Cache-control': `max-age=${DIRECTUS_CACHE_TTL_S}, s-maxage=${DIRECTUS_CACHE_TTL_S}`,
        Expires: new Date(Date.now() + DIRECTUS_CACHE_TTL_S * 1000).toUTCString(),
      };
      const headers = treated.data.headers ?? fallbackHeaders;
      context.res.setHeader('Cache-Control', headers['Cache-control']);
      context.res.setHeader('Expires', headers.Expires);
      context.res.statusCode = treated.statusCode;
      return {
        props: {
          responseData: treated.data,
          navBarData: responseMenu?.data?.data ?? null,
        },
      };
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { source: 'directus-materia-video' },
      extra: { canal: channel, slug },
    });
  }

  const mainReq = { promise: api.get<FeaturedNewsApi>(`/video/${channel}/${slug}/`), link: `/video/${channel}/${slug}/` };
  const responseMenuReq = { promise: responseMenuRequest, link: responseMenuLink };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: mainReq,
    navBarPromise: responseMenuReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const responseData = data;
  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;

  context.res.setHeader('Cache-Control', data.headers['Cache-control']);
  context.res.setHeader('Expires', data.headers.Expires);
  context.res.statusCode = statusCode;

  return {
    props: {
      responseData,
      navBarData,
    },
  };
};
